import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Box, IconButton, LinearProgress, Skeleton, Table, TableBody, TextField, Tooltip, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { sbg } from '../../theme/sbgTokens';
import type { StatusTone } from '../../theme/sbgTokens';
import { ErrorState, KpiGrid, SectionCard, TableCard, TableSkeletonRows, useDelayedFlag } from '../ui';
import { formatTime } from '../../utils/format';

/**
 * Shared building blocks for the manager team view (morning check-ins and
 * weekend shifts), built on the SBG UI kit and role tokens so they follow the
 * brand and dark mode.
 */

/* ------------------------------------------------------------------------ */
/* Status                                                                    */
/* ------------------------------------------------------------------------ */

/**
 * Server status labels arrive in title case ("Missed Check-In"); the UI is
 * sentence case. Abbreviations (AM, ET) and figures keep their form.
 */
export const sentenceCaseLabel = (label: string) =>
  label
    .split(' ')
    .map((word, index) => (index === 0 || /\d/.test(word) || /^[A-Z]{2,}$/.test(word) ? word : word.toLowerCase()))
    .join(' ');

/** Pill tone for a morning office check-in status. */
export const getCheckinStatusTone = (status: string): StatusTone => {
  switch (status) {
    case 'on_time':
      return 'success';
    case 'late_on_site':
    case 'email_unavailable':
      return 'warning';
    case 'offsite_click':
    case 'no_click':
      return 'danger';
    case 'not_expected':
    case 'no_record':
      return 'neutral';
    default:
      return 'info';
  }
};

/* ------------------------------------------------------------------------ */
/* Team view building blocks                                                 */
/* ------------------------------------------------------------------------ */

/** True when a rep's name or team contains the search text. */
export const matchesRepSearch = (rep: { name: string; teamName: string | null }, search: string) => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return rep.name.toLowerCase().includes(query) || (rep.teamName ?? '').toLowerCase().includes(query);
};

/** True when the reps span more than one team, so a Team column earns its space. */
export const spansSeveralTeams = (reps: Array<{ teamName: string | null }>) =>
  new Set(reps.map((rep) => rep.teamName).filter(Boolean)).size > 1;

/** The filtered-empty title that names what excluded every rep. */
export const noMatchTitle = (search: string) => `No reps match “${search.trim()}”`;

/** The parts of a query result the freshness line needs (an RTK Query result fits). */
interface FreshnessSource {
  data?: unknown;
  error?: unknown;
  isFetching?: boolean;
  fulfilledTimeStamp?: number;
}

/**
 * The header's freshness part: "Loading…" until every source has answered,
 * "Refreshing…" while one refetches, then "Updated 9:03 AM ET" (the oldest).
 */
export const freshnessLabel = (sources: FreshnessSource[]) => {
  if (sources.some((source) => !source.data && !source.error)) return 'Loading…';
  if (sources.some((source) => source.isFetching)) return 'Refreshing…';
  const stamps = sources
    .map((source) => (source.data ? source.fulfilledTimeStamp : undefined))
    .filter((stamp): stamp is number => typeof stamp === 'number');
  return stamps.length > 0 ? `Updated ${formatTime(Math.min(...stamps))}` : null;
};

/**
 * Reports a panel's header meta (scope · period · freshness) to the page that
 * owns the header. A layout effect, so the header never paints without it.
 */
export const useReportMeta = (report: ((parts: string[]) => void) | undefined, parts: string[]) => {
  const key = parts.join('\u0000');
  useLayoutEffect(() => {
    report?.(key ? key.split('\u0000') : []);
  }, [report, key]);
};

/** A segmented control's options stay on one line; its wrapper scrolls on a phone instead. */
export const nowrapSegmentsSx = {
  maxWidth: 'none',
  '& .MuiToggleButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
} as const;

/** Keeps the first (Rep) column in view while a wide table scrolls sideways (book §11.10). */
export const stickyFirstColumnSx = {
  '& .MuiTableCell-root:first-of-type': { position: 'sticky', left: 0, zIndex: 1, bgcolor: sbg.bg.surface },
  '& .MuiTableHead-root .MuiTableCell-root:first-of-type': { zIndex: 3, bgcolor: sbg.bg.surfaceSubtle },
  '& .MuiTableBody-root .MuiTableRow-root:hover > .MuiTableCell-root:first-of-type': { bgcolor: sbg.bg.hover },
} as const;

/** The Rep cell's minimum width: narrower on a phone, where it stays pinned. */
export const repCellSx = { minWidth: { xs: 160, sm: 200 } } as const;

/**
 * A 2px progress line that floats in the gap above refreshed content, so a
 * refetch never shifts the layout. Its parent must be `position: relative`.
 */
export const RefreshBar: React.FC<{ active: boolean; label: string }> = ({ active, label }) =>
  active ? (
    <LinearProgress
      aria-label={label}
      sx={{ position: 'absolute', top: -13, left: 0, right: 0, height: 2, borderRadius: sbg.radius.pill }}
    />
  ) : null;

/** Dims content that is being replaced, so stale figures never read as current. */
export const staleSx = (stale: boolean) => ({
  opacity: stale ? 0.56 : 1,
  transition: `opacity ${sbg.duration.fast} ${sbg.ease.standard}`,
});

/**
 * A section on the canvas: `heading-3` title, a supporting line, an optional
 * control on the right. Each section loads and refreshes on its own: while
 * `refreshing`, a progress line shows above its body and the old figures dim.
 */
export const TeamSection: React.FC<{
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** A tag beside the title (a holiday). */
  badge?: React.ReactNode;
  /** A view control for this section, such as its period. */
  action?: React.ReactNode;
  refreshing?: boolean;
  refreshLabel?: string;
  children: React.ReactNode;
}> = ({ id, title, description, badge, action, refreshing = false, refreshLabel = 'Refreshing', children }) => (
  <Box component="section" aria-labelledby={id} sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', columnGap: 3, rowGap: 1.5 }}>
      <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography id={id} variant="h3" component="h2" sx={{ color: sbg.text.primary }}>
            {title}
          </Typography>
          {badge}
        </Box>
        {description && (
          <Typography variant="body1" sx={{ color: sbg.text.secondary, mt: 0.5, maxWidth: '76ch' }}>
            {description}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0, maxWidth: '100%', overflowX: 'auto' }}>{action}</Box>}
    </Box>
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      <RefreshBar active={refreshing} label={refreshLabel} />
      <Box aria-busy={refreshing || undefined} sx={{ display: 'grid', gap: 3, minWidth: 0, ...staleSx(refreshing) }}>
        {children}
      </Box>
    </Box>
  </Box>
);

/** A small heading inside a table toolbar ("By rep"). */
export const TableTitle: React.FC<{ children: React.ReactNode; caption?: React.ReactNode }> = ({ children, caption }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, minWidth: 0 }}>
    <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
      {children}
    </Typography>
    {caption && (
      <Typography variant="body2" component="span" sx={{ color: sbg.text.tertiary }}>
        {caption}
      </Typography>
    )}
  </Box>
);

/** Quiet secondary text for a table toolbar ("2 reps", "Lowest on-time rate first"). */
export const ToolbarNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="span" sx={{ color: sbg.text.tertiary, fontVariantNumeric: 'tabular-nums' }}>
    {children}
  </Typography>
);

/**
 * A date field with previous/next steppers. The label sits above the field,
 * so the steppers align to its bottom edge.
 */
export const DateStepper: React.FC<{
  label: string;
  value: string;
  onChange: (ymd: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  previousLabel: string;
  nextLabel: string;
  max?: string;
  /** Why the next step is unavailable; disables the button and becomes its tooltip. */
  nextDisabledReason?: string | null;
  disabled?: boolean;
}> = ({ label, value, onChange, onPrevious, onNext, previousLabel, nextLabel, max, nextDisabledReason, disabled = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
    <Tooltip title={previousLabel} describeChild>
      <span>
        <IconButton aria-label={previousLabel} onClick={onPrevious} disabled={disabled}>
          <ChevronLeft />
        </IconButton>
      </span>
    </Tooltip>
    <TextField
      type="date"
      label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => event.target.value && onChange(event.target.value)}
      slotProps={{ htmlInput: { max } }}
      sx={{ width: 172 }}
    />
    <Tooltip title={nextDisabledReason || nextLabel} describeChild>
      <span>
        <IconButton aria-label={nextLabel} onClick={onNext} disabled={disabled || Boolean(nextDisabledReason)}>
          <ChevronRight />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

/* ------------------------------------------------------------------------ */
/* Loading, slow and stalled                                                 */
/* ------------------------------------------------------------------------ */

/** After this long, a first load says it is slow (it keeps waiting). */
export const SLOW_LOAD_MS = 12_000;
/** After this long, a request is treated as lost: the section stops waiting and offers a reload. */
export const STALLED_LOAD_MS = 30_000;

/**
 * True once a request for `key` has been pending for `delay` ms. RTK Query
 * ignores `refetch()` while a request is still pending and these requests
 * have no timeout, so a hung request can only be retried by reloading.
 */
export const useStalled = (pending: boolean, key: string, delay = STALLED_LOAD_MS) => {
  const [stalledKey, setStalledKey] = useState<string | null>(null);
  useEffect(() => {
    if (!pending) {
      setStalledKey(null);
      return undefined;
    }
    const timer = window.setTimeout(() => setStalledKey(key), delay);
    return () => window.clearTimeout(timer);
  }, [pending, key, delay]);
  return pending && stalledKey === key;
};

export const reloadPage = () => window.location.reload();

export const STALLED_BODY =
  'The attendance service hasn’t answered in 30 seconds. Reload the page to ask again. If it keeps happening, tell an admin.';

/** A one-line text placeholder (a section description), revealed after 300ms like the other skeletons. */
export const TextSkeleton: React.FC<{ width?: number | string }> = ({ width = 280 }) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Skeleton
      aria-hidden="true"
      variant="text"
      width={width}
      sx={{ display: 'inline-block', maxWidth: '100%', visibility: visible ? 'visible' : 'hidden' }}
    />
  );
};

/** A section's error where its figures would be: what happened, what to do, and a retry that works. */
export const SectionError: React.FC<{ title: string; body: string; onRetry: () => void; retryLabel?: string }> = ({
  title,
  body,
  onRetry,
  retryLabel,
}) => (
  <SectionCard>
    <ErrorState compact title={title} body={body} onRetry={onRetry} retryLabel={retryLabel} />
  </SectionCard>
);

/** A section whose request never answered. Reloading is the one retry that can work. */
export const SectionStalled: React.FC<{ title: string }> = ({ title }) => (
  <SectionError title={title} body={STALLED_BODY} retryLabel="Reload page" onRetry={reloadPage} />
);

const SkeletonTile: React.FC = () => (
  <Box
    sx={{
      display: 'grid',
      gap: 1.25,
      p: 2.5,
      borderRadius: sbg.radius.card,
      border: `1px solid ${sbg.border.default}`,
      bgcolor: sbg.bg.surface,
    }}
  >
    <Skeleton variant="rounded" width="45%" height={12} />
    <Skeleton variant="rounded" width="35%" height={32} />
    <Skeleton variant="rounded" width="60%" height={12} />
  </Box>
);

const SkeletonSpecGrid: React.FC = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', columnGap: 4, rowGap: 3 }}>
    {[0, 1, 2, 3].map((cell) => (
      <Box key={cell} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1 }}>
        <Skeleton variant="rounded" width="50%" height={12} />
        <Skeleton variant="rounded" width="35%" height={28} />
        <Skeleton variant="rounded" width="70%" height={12} />
      </Box>
    ))}
  </Box>
);

/**
 * A section's first load: skeletons at its real layout (figures, an optional
 * card, then its tables), revealed after 300ms. A load that runs long says so
 * in the table's toolbar, where the real toolbar will sit; it offers no retry,
 * because a pending request cannot be retried (see `useStalled`).
 */
export const SectionLoading: React.FC<{
  label: string;
  /** The figures above the table: KPI tiles or a spec grid. */
  summary: 'tiles' | 'spec';
  columns: number;
  /** A card between the figures and the table (the shift fill). */
  card?: boolean;
  tables?: number;
}> = ({ label, summary, columns, card = false, tables = 1 }) => {
  const visible = useDelayedFlag(true, 300);
  const slow = useDelayedFlag(true, SLOW_LOAD_MS);
  return (
    <Box role="status" aria-label={label} aria-busy="true" sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      <span className="sbg-sr-only">{label}</span>
      <Box sx={{ display: 'grid', gap: 3, minWidth: 0, visibility: visible ? 'visible' : 'hidden' }}>
        <Box aria-hidden="true">
          {summary === 'tiles' ? (
            <KpiGrid phoneColumns={2} sx={{ mb: 0 }}>
              {[0, 1, 2, 3].map((tile) => (
                <SkeletonTile key={tile} />
              ))}
            </KpiGrid>
          ) : (
            <SkeletonSpecGrid />
          )}
        </Box>
        {card && (
          <Box aria-hidden="true">
            <Skeleton variant="rounded" height={112} sx={{ borderRadius: sbg.radius.lg }} />
          </Box>
        )}
        {Array.from({ length: tables }).map((_, table) => (
          <TableCard
            key={table}
            toolbar={
              <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                {slow && table === 0 ? (
                  <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
                    Still loading. The attendance service is slow to respond.
                  </Typography>
                ) : (
                  <Skeleton aria-hidden="true" variant="rounded" width={160} height={14} />
                )}
              </Box>
            }
          >
            <Box aria-hidden="true">
              <Box sx={{ height: 36, bgcolor: sbg.bg.surfaceSubtle, borderBottom: `1px solid ${sbg.border.default}` }} />
              <Table>
                <TableBody>
                  <TableSkeletonRows columns={columns} rows={table === 0 ? 6 : 4} delay={0} />
                </TableBody>
              </Table>
            </Box>
          </TableCard>
        ))}
      </Box>
    </Box>
  );
};
